import React, { useState, useEffect } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { useLazyCommand } from "@soap/modules";
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { 
  ChevronDown, 
  ChevronRight, 
  Plus, 
  Trash2, 
  GripVertical,
  Edit,
  List,
  ToggleLeft,
  Type,
  ChevronsDown,
  Square,
  PenTool,
  Link,
  ArrowDown
} from 'lucide-react';
import type { Assessment, Section, Question } from './types';
import { timingModeKeys } from './types';
import { calcPoints, getDefaultSectionTime, getDefaultQuestionTime } from './utils';
import { InlineEditNumber } from './InlineEditNumber';
import { InlineEditTime } from './InlineEditTime';
import { RemoveSectionDialog } from './RemoveSectionDialog';
import { RemoveQuestionDialog } from './RemoveQuestionDialog';

// Helper function to get question type icon and label
function getQuestionTypeInfo(questionTypeKey?: string): { icon: React.ReactNode; label: string } {
  const iconProps = { size: 16, className: "text-gray-600" };
  
  switch (questionTypeKey) {
    case 'multiple-choice':
      return { icon: <List {...iconProps} />, label: 'Multiple Choice' };
    case 'boolean':
      return { icon: <ToggleLeft {...iconProps} />, label: 'True/False' };
    case 'freetext':
      return { icon: <Type {...iconProps} />, label: 'Free Text' };
    case 'single-selection':
      return { icon: <ChevronsDown {...iconProps} />, label: 'Single Selection' };
    case 'specific-string':
      return { icon: <Square {...iconProps} />, label: 'Specific String' };
    case 'fill-in-the-blank':
      return { icon: <PenTool {...iconProps} />, label: 'Fill in Blank' };
    case 'matching':
      return { icon: <Link {...iconProps} />, label: 'Matching' };
    case 'sequence':
      return { icon: <ArrowDown {...iconProps} />, label: 'Sequence' };
    default:
      return { icon: <Type {...iconProps} />, label: 'Unknown' };
  }
}

export function SectionItem({ 
  section, 
  assessment, 
  onUpdate,
  existingQuestionIds,
  index,
  onDragEnd,
  expanded: expandedProp,
  onExpandedChange,
  onShowQuestionWizard,
  onEditQuestion,
  onRefresh
}: { 
  section: Section; 
  assessment: Assessment;
  onUpdate: (assessment: Assessment) => void;
  existingQuestionIds: string[];
  index: number;
  onDragEnd: (result: any) => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  onShowQuestionWizard: (sectionId: string) => void;
  onEditQuestion: (question: Question, sectionId: string) => void;
  onRefresh: () => void;
}) {
  const [expanded, setExpanded] = useState(expandedProp ?? true);
  
  // Sync internal state with prop when prop changes
  useEffect(() => {
    if (expandedProp !== undefined) {
      setExpanded(expandedProp);
    }
  }, [expandedProp]);
  const [showRemoveSection, setShowRemoveSection] = useState(false);
  const [questionToRemove, setQuestionToRemove] = useState<{ id: string; title: string } | null>(null);
  const [updatePointsTrigger] = useLazyCommand("C196v1_UpdateAssessmentPointsValue");
  const [updateTimingsTrigger] = useLazyCommand("C195v1_UpdateAssessmentTimeLimits");

  const handlePointsUpdate = (questionId: string, newPoints: number) => {
    const cloned = JSON.parse(JSON.stringify(assessment));
    const question = cloned.Sections.flatMap((s: Section) => s.Questions).find((q: Question) => q.QuestionId === questionId);
    if (question) {
      question.Points3 = newPoints;
      calcPoints(cloned);
      onUpdate(cloned);
      
      updatePointsTrigger({
        $type: "C196v1_UpdateAssessmentPointsValue",
        C196_AssessmentId: assessment.AssessmentId,
        C196_QuestionId: questionId,
        C196_Points: newPoints,
      });
    }
  };

  const handleSectionTimeLimitUpdate = (newTimeLimit: number) => {
    // Only allow updating section time limits when timing mode is per-section
    if (assessment.TimingMode !== timingModeKeys.PerSection) {
      return;
    }
    
    const cloned = JSON.parse(JSON.stringify(assessment));
    const sectionToUpdate = cloned.Sections.find((s: Section) => s.SectionId === section.SectionId);
    if (sectionToUpdate) {
      sectionToUpdate.TimeLimit = newTimeLimit;
      onUpdate(cloned);
      
      updateTimingsTrigger({
        $type: "C195v1_UpdateAssessmentTimeLimits",
        C195_AssessmentId: assessment.AssessmentId,
        C195_AssessmentTimingModeKey: timingModeKeys.PerSection,
        C195_SectionId: section.SectionId,
        C195_TimeInSeconds: newTimeLimit,
      });
    }
  };

  const handleQuestionTimeLimitUpdate = (questionId: string, newTimeLimit: number) => {
    // Only allow updating question time limits when timing mode is per-question
    if (assessment.TimingMode !== timingModeKeys.PerQuestion) {
      return;
    }
    
    const cloned = JSON.parse(JSON.stringify(assessment));
    const question = cloned.Sections.flatMap((s: Section) => s.Questions).find((q: Question) => q.QuestionId === questionId);
    if (question) {
      question.TimeLimit2 = newTimeLimit;
      onUpdate(cloned);
      
      updateTimingsTrigger({
        $type: "C195v1_UpdateAssessmentTimeLimits",
        C195_AssessmentId: assessment.AssessmentId,
        C195_AssessmentTimingModeKey: timingModeKeys.PerQuestion,
        C195_QuestionId: questionId,
        C195_TimeInSeconds: newTimeLimit,
      });
    }
  };

  return (
    <Draggable draggableId={section.SectionId} index={index} type="droppableItem">
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`mb-3 ${snapshot.isDragging ? 'opacity-50' : ''}`}
        >
          <Card>
            <div 
              className="p-4 cursor-pointer hover:bg-gray-50 transition-colors flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3"
              onClick={() => {
                const newExpanded = !expanded;
                setExpanded(newExpanded);
                if (onExpandedChange) {
                  onExpandedChange(newExpanded);
                }
              }}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div {...provided.dragHandleProps}>
                  <GripVertical className="text-gray-400" size={20} />
                </div>
                {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                <div className="min-w-0">
                  <h3 className="font-semibold">{section.Title}</h3>
                  <p className="text-sm text-gray-600">
                    {section.Questions.length} question{section.Questions.length !== 1 ? 's' : ''} • {section.Points2} points
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                {assessment.TimingMode === timingModeKeys.PerSection && (
                  <div className="flex items-center gap-2 mr-4">
                    <span className="text-sm text-gray-600">Time:</span>
                    <InlineEditTime 
                      value={section.TimeLimit} 
                      onSave={handleSectionTimeLimitUpdate}
                      useDefault={true}
                      defaultTime={getDefaultSectionTime(section.Questions?.length || 0)}
                    />
                  </div>
                )}
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    onShowQuestionWizard(section.SectionId);
                  }}
                >
                  <Plus size={16} className="mr-1" /> Question
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowRemoveSection(true)}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
            
            {expanded && (
              <Droppable droppableId={section.SectionId} type="droppableSubItem">
                {(provided, snapshot) => (
                  <div 
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`border-t ${snapshot.isDraggingOver ? 'bg-action-blue/10' : ''}`}
                  >
                    {section.Questions.length > 0 ? (
                      <div className="w-full">
                        {/* Header */}
                        <div className={`bg-gray-50 border-b grid gap-4 px-3 py-3 pr-6 ${
                          assessment.TimingMode === timingModeKeys.PerQuestion 
                            ? 'grid-cols-[1fr_auto_auto_auto_auto] lg:grid-cols-[1fr_auto_auto_auto_auto]' 
                            : 'grid-cols-[1fr_auto_auto_auto] lg:grid-cols-[1fr_auto_auto_auto]'
                        } hidden lg:grid`}>
                          <div className="text-left text-sm font-medium text-gray-700">Question</div>
                          <div className="flex items-center justify-center text-sm font-medium text-gray-700 w-20">Type</div>
                          <div className="flex items-center text-sm font-medium text-gray-700 w-24">Points</div>
                          {assessment.TimingMode === timingModeKeys.PerQuestion && (
                            <div className="flex items-center text-sm font-medium text-gray-700 w-32">Time</div>
                          )}
                          <div className="w-20"></div>
                        </div>
                        {/* Question rows */}
                        {section.Questions.map((question, idx) => (
                          <Draggable key={question.QuestionId} draggableId={question.QuestionId} index={idx} type="droppableSubItem">
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={provided.draggableProps.style}
                                className={`border-b hover:bg-gray-50 ${
                                  snapshot.isDragging ? 'bg-white shadow-lg rounded' : ''
                                }`}
                              >
                                {/* Mobile/Tablet layout */}
                                <div className="lg:hidden flex flex-col gap-3 px-3 py-3">
                                  <div className="flex items-start gap-2 min-w-0">
                                    <div 
                                      {...provided.dragHandleProps}
                                      className="cursor-grab active:cursor-grabbing touch-none select-none flex-shrink-0 mt-0.5"
                                    >
                                      <GripVertical className="text-gray-400" size={16} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <span className="text-sm font-medium text-gray-700">Question: </span>
                                      <span className="text-sm break-words">{question.Title}</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between gap-4 pl-6 flex-wrap">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-gray-600">Type:</span>
                                      <div 
                                        className="flex items-center gap-1 cursor-help" 
                                        title={getQuestionTypeInfo(question.QuestionTypeKey).label}
                                      >
                                        {getQuestionTypeInfo(question.QuestionTypeKey).icon}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-gray-600">Points:</span>
                                      <InlineEditNumber 
                                        value={question.Points3} 
                                        min={1}
                                        max={100}
                                        onSave={(val) => handlePointsUpdate(question.QuestionId, val)}
                                      />
                                    </div>
                                    {assessment.TimingMode === timingModeKeys.PerQuestion && (
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-600">Time:</span>
                                        <InlineEditTime 
                                          value={question.TimeLimit2} 
                                          onSave={(val) => handleQuestionTimeLimitUpdate(question.QuestionId, val)}
                                          useDefault={true}
                                          defaultTime={getDefaultQuestionTime()}
                                        />
                                      </div>
                                    )}
                                    <div className="flex items-center gap-1">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                          onEditQuestion(question, section.SectionId);
                                        }}
                                        title="Edit Question"
                                      >
                                        <Edit size={16} className="text-gray-600" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setQuestionToRemove({ id: question.QuestionId, title: question.Title })}
                                        title="Delete Question"
                                      >
                                        <Trash2 size={16} className="text-destructive-action" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                                {/* Desktop layout */}
                                <div className={`hidden lg:grid gap-4 px-3 py-3 pr-6 ${
                                  assessment.TimingMode === timingModeKeys.PerQuestion 
                                    ? 'grid-cols-[1fr_auto_auto_auto_auto]' 
                                    : 'grid-cols-[1fr_auto_auto_auto]'
                                }`}>
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div 
                                      {...provided.dragHandleProps}
                                      className="cursor-grab active:cursor-grabbing touch-none select-none flex-shrink-0"
                                    >
                                      <GripVertical className="text-gray-400" size={16} />
                                    </div>
                                    <span className="text-sm break-words">{question.Title}</span>
                                  </div>
                                  <div 
                                    className="flex items-center justify-center w-20 cursor-help" 
                                    title={getQuestionTypeInfo(question.QuestionTypeKey).label}
                                  >
                                    {getQuestionTypeInfo(question.QuestionTypeKey).icon}
                                  </div>
                                  <div className="flex items-center w-24">
                                    <InlineEditNumber 
                                      value={question.Points3} 
                                      min={1}
                                      max={100}
                                      onSave={(val) => handlePointsUpdate(question.QuestionId, val)}
                                    />
                                  </div>
                                  {assessment.TimingMode === timingModeKeys.PerQuestion && (
                                    <div className="flex items-center w-32">
                                      <InlineEditTime 
                                        value={question.TimeLimit2} 
                                        onSave={(val) => handleQuestionTimeLimitUpdate(question.QuestionId, val)}
                                        useDefault={true}
                                        defaultTime={getDefaultQuestionTime()}
                                      />
                                    </div>
                                  )}
                                  <div className="flex items-center gap-1 w-20">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        onEditQuestion(question, section.SectionId);
                                      }}
                                      title="Edit Question"
                                    >
                                      <Edit size={16} className="text-gray-600" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setQuestionToRemove({ id: question.QuestionId, title: question.Title })}
                                      title="Delete Question"
                                    >
                                      <Trash2 size={16} className="text-destructive-action" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        <p className="text-sm">No questions yet. Click "Add Question" to get started.</p>
                      </div>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            )}
          </Card>

          {showRemoveSection && (
            <RemoveSectionDialog
              assessmentId={assessment.AssessmentId}
              sectionId={section.SectionId}
              sectionTitle={section.Title}
              onClose={() => setShowRemoveSection(false)}
            />
          )}

          {questionToRemove && (
            <RemoveQuestionDialog
              assessmentId={assessment.AssessmentId}
              questionId={questionToRemove.id}
              questionTitle={questionToRemove.title}
              timingMode={assessment.TimingMode}
              sectionTimeLimit={section.TimeLimit}
              sectionQuestionCount={section.Questions.length}
              sectionId={section.SectionId}
              overallTimeLimit={assessment.OverallTimeLimit}
              totalQuestionCount={assessment.Sections.flatMap(s => s.Questions).length}
              onClose={() => setQuestionToRemove(null)}
              onSectionTimeUpdate={(newTime: number) => {
                // Only send command, don't update local state
                // The dialog will refresh after this completes
                updateTimingsTrigger({
                  $type: "C195v1_UpdateAssessmentTimeLimits",
                  C195_AssessmentId: assessment.AssessmentId,
                  C195_AssessmentTimingModeKey: timingModeKeys.PerSection,
                  C195_SectionId: section.SectionId,
                  C195_TimeInSeconds: newTime,
                });
              }}
              onOverallTimeUpdate={(newTime: number) => {
                // Only send command, don't update local state
                // The dialog will refresh after this completes
                updateTimingsTrigger({
                  $type: "C195v1_UpdateAssessmentTimeLimits",
                  C195_AssessmentId: assessment.AssessmentId,
                  C195_AssessmentTimingModeKey: timingModeKeys.PerAssessment,
                  C195_TimeInSeconds: newTime,
                });
              }}
              onRefresh={onRefresh}
            />
          )}
        </div>
      )}
    </Draggable>
  );
}

